/**
 * Локальные предпочтения гостя в embed (WebView / браузер): последняя выбранная организация в глобальном каталоге
 * и фильтр филиала по organization_id для расписания.
 *
 * Мобильный клиент (Capacitor): храните те же ключи в AsyncStorage / SharedPreferences
 * и подставляйте в deep link / query при старте; при необходимости синхронизируйте с бэкендом (профиль пользователя),
 * если нужны настройки между устройствами.
 */

const LAST_GUEST_ORG_KEY = 'domcrm_embed_last_guest_org_id';

export const EMBED_BRANCH_PREFS_CHANGED_EVENT = 'domcrm:embed-branch-prefs-changed';

export function notifyEmbedBranchPrefsChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EMBED_BRANCH_PREFS_CHANGED_EVENT));
}

export type EmbedBranchFilterPrefs = {
  branchId: number | null;
  allBranches: boolean;
  /** Филиал по умолчанию при первом открытии расписания (скрепка); сохраняется при выборе «все филиалы». */
  defaultBranchId: number | null;
};

function orgBranchKey(organizationId: number): string {
  return `domcrm_embed_branch_filter_org_${organizationId}`;
}

export function getLastGuestOrganizationId(): number | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LAST_GUEST_ORG_KEY);
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function setLastGuestOrganizationId(organizationId: number): void {
  if (typeof localStorage === 'undefined') return;
  if (!Number.isFinite(organizationId) || organizationId <= 0) return;
  try {
    localStorage.setItem(LAST_GUEST_ORG_KEY, String(organizationId));
  } catch {
    /* quota / private mode */
  }
}

export function loadBranchFilterPrefs(organizationId: number): EmbedBranchFilterPrefs | null {
  if (typeof localStorage === 'undefined' || organizationId <= 0) return null;
  try {
    const raw = localStorage.getItem(orgBranchKey(organizationId));
    if (raw == null || raw === '') return null;
    const o = JSON.parse(raw) as { branchId?: unknown; allBranches?: unknown; defaultBranchId?: unknown };
    const branchId =
      typeof o.branchId === 'number' && Number.isFinite(o.branchId)
        ? o.branchId
        : typeof o.branchId === 'string'
          ? parseInt(o.branchId, 10)
          : null;
    const bid = branchId != null && Number.isFinite(branchId) && branchId > 0 ? branchId : null;
    const allBranches = o.allBranches === true;
    let dbid: number | null = null;
    if (typeof o.defaultBranchId === 'number' && Number.isFinite(o.defaultBranchId) && o.defaultBranchId > 0) {
      dbid = o.defaultBranchId;
    } else if (typeof o.defaultBranchId === 'string') {
      const n = parseInt(o.defaultBranchId, 10);
      dbid = Number.isFinite(n) && n > 0 ? n : null;
    }
    return { branchId: bid, allBranches, defaultBranchId: dbid };
  } catch {
    return null;
  }
}

type SaveBranchFilterInput = {
  branchId: number | null;
  allBranches: boolean;
  /** Если не передать — сохраняется прежнее значение из storage. */
  defaultBranchId?: number | null;
};

export function saveBranchFilterPrefs(organizationId: number, prefs: SaveBranchFilterInput): void {
  if (typeof localStorage === 'undefined' || organizationId <= 0) return;
  try {
    const prev = loadBranchFilterPrefs(organizationId);
    const defaultBranchId =
      prefs.defaultBranchId !== undefined ? prefs.defaultBranchId : (prev?.defaultBranchId ?? null);
    const merged: EmbedBranchFilterPrefs = {
      branchId: prefs.branchId,
      allBranches: prefs.allBranches,
      defaultBranchId: defaultBranchId ?? null,
    };
    localStorage.setItem(orgBranchKey(organizationId), JSON.stringify(merged));
    notifyEmbedBranchPrefsChanged();
  } catch {
    /* ignore */
  }
}

/** Переключение «филиал по умолчанию» для расписания (скрепка на странице филиалов). */
export function toggleDefaultScheduleBranch(organizationId: number, branchId: number): void {
  if (typeof localStorage === 'undefined' || organizationId <= 0 || branchId <= 0) return;
  const prev = loadBranchFilterPrefs(organizationId);
  const nextDefault = prev?.defaultBranchId === branchId ? null : branchId;
  saveBranchFilterPrefs(organizationId, {
    branchId: prev?.branchId ?? null,
    allBranches: prev?.allBranches ?? false,
    defaultBranchId: nextDefault,
  });
}
